import React from 'react';
import styled from 'styled-components';

const Line = styled.div<{
	fontSize: number;
	lineHeight: number;
	isFirst: boolean;
}>`
	font-size: ${({ fontSize }) => fontSize}px;
	line-height: ${({ lineHeight }) => lineHeight}px;
	${({ isFirst }) => isFirst && 'margin-top: 0;'}

	width: 100%;
	text-align: center;
	white-space: nowrap;

	@media screen and (min-width: 1280px) {
		align-items: center;
		align-content: center;
	}

	@media screen and (max-width: 560px) {
		font-size: ${({ fontSize }) => fontSize * 1.2}px;
		overflow: visible;
	}
`;

const TextContainer = styled.div`
	font-family: var(--font-family-head);
	font-weight: bold;
	color: white;
	z-index: -2;

	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	align-content: center;
	overflow: hidden;
	gap: 6%;
	position: absolute;
	top: 8%;
	left: 50%;
	transform: translate(-50%, -15%);
	white-space: nowrap;
	width: max-content;
	height: 100%;

	@media screen and (max-width: 560px) {
		gap: 7%;
		overflow: visible;
	}

	@media screen and (min-width: 1280px) {
		gap: 7%;
		overflow: visible;
	}
`;

const ModelTitleBackground = ({ text }: { text: string }): React.ReactElement => {
	const mergeLastTwoWords = (text: string): string[] => {
		if (!text) return [];

		// SHOOTING BRAKE: Reemplazar "shooting brake", si hay,  por marcador temporal 'shooting_brake" para evitar la separación
		const normalizedText = text.replace(/shooting brake/gi, 'shooting_brake');

		const words = normalizedText.split(' ');

		// Si la segunda palabra es corta (<3 letras) y hay más palabras,
		// ponemos la primera palabra en la primera línea,
		// y la segunda + resto en la segunda línea
		if (words.length >= 3 && words[1].length < 3) {
			const firstLine = words[0].replace(/_/g, '\u00A0').toUpperCase();
			const secondLine = words.slice(1).join(' ').replace(/_/g, '\u00A0').toUpperCase();
			return [firstLine, secondLine];
		}

		// Si solo hay dos palabras y la segunda es corta, unimos todo en una línea
		if (words.length >= 2 && words[1].length < 3) {
			const singleLine = normalizedText.replace(/_/g, '\u00A0').toUpperCase();
			return [singleLine];
		}

		// Por defecto, devolver las dos primeras palabras como líneas separadas
		const limitedWords = words.slice(0, 2).map((w) => w.replace(/_/g, '\u00A0').toUpperCase());

		return limitedWords;
	};

	const words = React.useMemo(() => mergeLastTwoWords(text), [text]);

	const [fontSizes, setFontSizes] = React.useState<number[]>([]);
	const [lineHeight, setLineHeight] = React.useState(0);
	const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);

	React.useEffect(() => {
		const handleResize = () => setWindowWidth(window.innerWidth);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	React.useEffect(() => {
		const calculateSizes = () => {
			const sizes = words.map((word, index) => {
				const testDiv = document.createElement('div');
				testDiv.style.visibility = 'hidden';
				testDiv.style.position = 'absolute';
				testDiv.style.whiteSpace = 'nowrap';
				testDiv.style.fontSize = '100px';
				testDiv.innerHTML = word;
				document.body.appendChild(testDiv);

				const width = testDiv.offsetWidth;

				let fontSize: number;
				const maxFontSize = 500;

				if (windowWidth < 376) {
					fontSize = Math.min((windowWidth / width) * 85, maxFontSize);
				} else if (windowWidth < 561) {
					fontSize = Math.min((windowWidth / width) * 78, maxFontSize);
				} else if (windowWidth < 961) {
					fontSize = Math.min((windowWidth / width) * 70, maxFontSize);
				} else if (windowWidth < 1281) {
					fontSize = Math.min((windowWidth / width) * 55, maxFontSize);
				} else {
					fontSize = Math.min((windowWidth / width) * 50, maxFontSize);
				}

				// Reducción si la palabra tiene <=4 letras
				if (words.length > 1) {
					const firstShort = words[0].length <= 4;
					const secondShort = words[1]?.length <= 4;

					if (firstShort && secondShort) {
						fontSize *= 0.65;
					} else if (index === 0 && firstShort) {
						fontSize *= 0.8;
					} else if (index === 1 && secondShort) {
						fontSize *= 0.8;
					}
				}

				document.body.removeChild(testDiv);
				return fontSize;
			});

			setFontSizes(sizes);

			// Ajuste lineHeight en proporción al fontSize según resolución
			let lineHeightRatio = 0.75;
			if (windowWidth <= 375) {
				lineHeightRatio = 0.75;
			} else if (windowWidth < 580) {
				lineHeightRatio = 0.7;
			} else if (windowWidth < 960) {
				lineHeightRatio = 0.72;
			} else if (windowWidth < 1280) {
				lineHeightRatio = 0.75;
			} else {
				lineHeightRatio = 0.78;
			}

			const maxFontSize = Math.max(...sizes);
			setLineHeight(maxFontSize * lineHeightRatio);
		};

		calculateSizes();
	}, [text, words, windowWidth]);

	return (
		<TextContainer id="title-background-text-container">
			{words.map((word, index) => (
				<Line
					className="title-background-line"
					key={index}
					fontSize={fontSizes[index]}
					lineHeight={lineHeight}
					isFirst={index === 0}
				>
					{word.toLocaleUpperCase()}
				</Line>
			))}
		</TextContainer>
	);
};

export default ModelTitleBackground;
